import React, {useCallback, useContext, useEffect, useState} from 'react';
import {flatten, uniq} from "lodash";

import {recordings} from "../../../services/api";
import {DataPageContext} from "../context";

import {ElemsContext, Highlight} from '@auravisionlabs/aura-react';
import {EditShapes} from "./elems";


const OneRec = ({recid, search}) => {
    const {setLoading, setNav, setControls} = useContext(DataPageContext);
    const [editing, setEditing] = useState(false);
    const [taxonomiesInUse, setTaxonomiesInUse] = useState(null);
    const [keysAndValuesInUse, setKeysAndValuesInUse] = useState(null);
    const [loadingTaxonomies, setLoadingTaxonomies] = useState(false);

    // ElemsContext state — this component is the Provider
    const [recording, setRecording] = useState(null);
    const [elems, setElems] = useState(null);
    const [selected, _setSelected] = useState(null);
    const [canvasMode, setCanvasMode] = useState(null);
    const [hovered, setHovered] = useState(null);

    const fetchRecording = useCallback((id) => {
        console.log(`Fetching recording: ${id}`);
        setRecording(null);
        setElems(null);
        _setSelected(null);
        return recordings.get(
            id, {include: 'organisationDetails,locationDetails,cameraServerDetails,statElems,singleThumbnail,lineContexts,areaContexts'}
        ).then(rec => {
            setRecording(rec);
            return rec;
        });
    }, []);

    const clearRecording = useCallback(() => {
        setRecording(null);
        setElems(null);
        _setSelected(null);
    }, []);

    const setSelected = useCallback((selection) => {
        _setSelected(prev => {
            if (prev) prev.highlight = Highlight.NONE;
            if (selection) [selection.highlight, selection.visible] = [Highlight.GREEN, true];
            return selection;
        });
    }, []);

    // editing toggle — update controls
    const hasThumbnail = !!recording?.thumbnails?.[0]?.url;
    useEffect(() => {
        if (!hasThumbnail) {
            setControls([]);
            return () => setControls([]);
        }
        setControls([
            {name: editing ? "Save" : "Edit", onClick: () => setEditing(prev => !prev), icon: editing ? "floppy-disk" : "edit", confirm: editing}
        ]);
        return () => setControls([]);
    }, [editing, hasThumbnail]);

    // load recording data on mount
    useEffect(() => {
        console.log("Loading recid: " + recid);
        setLoading({status: true, location: "Loading camera: " + recid});

        fetchRecording(recid).then(rec => {
            if (!rec) throw "Recid not found!";
            setLoading({status: false, location: rec.name + " (" + rec.id + ")"});
            setNav({
                left: [
                    ...rec.organisation?.name ? [{
                        name: rec.organisation.name,
                        path: "/orgs/" + rec.organisation._id,
                        icon: "office",
                        subpaths: [
                            {name: "Locations", icon: "area-of-interest", path: '/locs?org=' + rec.organisation._id},
                            {name: "Devices", icon: "desktop", path: '/devs?org=' + rec.organisation._id},
                            {name: "Cameras", icon: "camera", path: '/recs?org=' + rec.organisation._id},
                            {name: "Users", icon: "people", path: '/orgs/' + rec.organisation._id + '/users'},
                        ]
                    }] : [],
                    ...rec.location?.name ? [{
                        name: rec.location.name,
                        path: "/locs/" + rec.location._id,
                        icon: "area-of-interest",
                        subpaths: [
                            {name: "Devices", icon: "desktop", path: '/devs?loc=' + rec.location._id},
                            {name: "Cameras", icon: "camera", path: '/recs?loc=' + rec.location._id},
                        ]
                    }] : [],
                    ...rec.cameraServer?.name ? [{
                        name: rec.cameraServer.name,
                        path: "/devs/" + rec.cameraServer._id,
                        icon: "desktop",
                        subpaths: [
                            {name: "Cameras", icon: "camera", path: '/recs?dev=' + rec.cameraServer._id},
                        ]
                    }] : [],
                ],
            });
        }).catch(error => {
            console.log(error);
            setLoading({status: false, location: "Failed to load camera..."});
        });

        return () => clearRecording();
    }, []);

    // load extra resources (taxonomies, keys and values) when recording changes
    useEffect(() => {
        if (recording?.organisation) {
            setLoading({status: false, location: recording.name + " (" + recording.id + ")" + " ... loading extra resources"});
            console.log("Loading extra resources...");
            setLoadingTaxonomies(true);
            recordings.list({
                include: 'areaContexts,lineContexts', withinOrganisations: recording.organisation._id
            }).then(siblings => {
                const allAreaContexts = flatten(siblings.map((r) => r.areaContexts)).filter((ac) => !!ac);
                const allLineContexts = flatten(siblings.map((r) => r.lineContexts)).filter((ac) => !!ac);
                setTaxonomiesInUse(uniq(allAreaContexts.map((ac) => ac.taxonomy)));
                setKeysAndValuesInUse(
                    allLineContexts.filter(
                        (lineContext) =>
                            lineContext.type === 'within-location-entry' ||
                            lineContext.type === 'within-location-pass-by'
                    ).reduce(
                        (finalKeysAndValues, lineContext) => {
                            lineContext.metadata.forEach(({key, value}) => {
                                finalKeysAndValues.keys = uniq([
                                    ...finalKeysAndValues.keys,
                                    key,
                                ]);
                                finalKeysAndValues.values = uniq([
                                    ...finalKeysAndValues.values,
                                    value,
                                ]);
                            });
                            return finalKeysAndValues;
                        },
                        {
                            keys: [],
                            values: [],
                        }
                    )
                );
                setLoading({status: false, location: recording.name + " (" + recording.id + ")"});
                console.log("Loaded extra resources...");
            }).catch(error => {
                console.log(error);
                setLoading({status: false, location: recording.name + " (" + recording.id + ")" + " ... failed to load extra resources"});
            }).finally(() => {
                setLoadingTaxonomies(false);
            });
        }
    }, [recording]);

    return (
        <ElemsContext.Provider value={{
            recording, elems, selected, canvasMode, hovered,
            fetch: fetchRecording, clear: clearRecording,
            setElems, setSelected, setCanvasMode, setHovered,
        }}>
            <EditShapes
                currentActiveMenuSection={search.get('currentActiveMenuSection')}
                taxonomiesInUse={taxonomiesInUse}
                keysAndValuesInUse={keysAndValuesInUse}
                loadingTaxonomies={loadingTaxonomies}
            />
        </ElemsContext.Provider>
    );
}

export {OneRec};